import { redirect } from 'react-router-dom';
import type { LoaderFunctionArgs } from 'react-router-dom';
import { PORTAL_ROUTES } from '@tasama/config';
import { usePageLoaderStore } from '@tasama/ui';
import {
  userContextService,
  USER_CONTEXT_QUERY_KEY,
} from '@/services/user-context.service';
import { useUserContextStore } from '@/stores/user-context.store';
import { useAuthStore } from '@/stores/auth-store';
import { queryClient } from '@/config/api-config';
import { KybStatus, NdaStatus, MsaStatus } from '@/types/user-context.types';
import {
  kybStatusService,
  ndaStatusService,
  type BackendKybStatusResponse,
  type BackendNdaStatusResponse,
} from '@/services';

/**
 * Ensure auth state is initialized before proceeding with protected route loaders
 * This is critical because route loaders run before React components mount,
 * so the auth store might not be initialized yet even though httpOnly cookies are valid.
 */
const ensureAuthInitialized = async (): Promise<boolean> => {
  const authState = useAuthStore.getState();

  // If already authenticated, we're good
  if (authState.isAuthenticated) {
    return true;
  }

  // If auth check is already in progress, wait for it
  if (authState.loading) {
    // Poll until loading completes (max 5 seconds)
    const maxWait = 5000;
    const pollInterval = 50;
    let waited = 0;

    while (authState.loading && waited < maxWait) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
      waited += pollInterval;
    }

    return useAuthStore.getState().isAuthenticated;
  }

  // No auth check in progress and not authenticated — trigger it now
  await authState.checkAuthStatus();
  return useAuthStore.getState().isAuthenticated;
};

/**
 * Shared loader helper for routes that need fresh user-context data
 * Fetches user-context from API on every access to ensure loaders have up-to-date status
 * Used by: KYB, NDA, MSA (initiate/review), Company Edit routes
 *
 * NOTE: Does NOT control page loader — caller is responsible for show/hide
 * This allows composite loaders to keep the loader visible across multiple async operations
 */
export const refreshUserContextLoader = async (fromPath?: string) => {
  try {
    // CRITICAL: Ensure auth is initialized before making API calls
    // This prevents "User is not authenticated" errors when loaders run
    // before the AuthProvider has completed its checkAuthStatus() call
    const isAuthenticated = await ensureAuthInitialized();

    if (!isAuthenticated) {
      // Not authenticated — redirect to login, preserving the intended destination
      // so LoginPage can send the user back after a successful sign-in.
      if (fromPath) {
        return redirect(
          `${PORTAL_ROUTES.LOGIN}?from=${encodeURIComponent(fromPath)}`
        );
      }
      return redirect(PORTAL_ROUTES.LOGIN);
    }

    // Only force-refresh if cached data is older than 30 s.
    // Callers that need guaranteed-fresh data (e.g. after a status-changing action)
    // should call invalidateUserContextCache() before navigating — that clears the
    // React Query entry and makes cacheAge infinite, triggering a fresh fetch here.
    const queryState = queryClient.getQueryState(USER_CONTEXT_QUERY_KEY);
    const cacheAge = queryState?.dataUpdatedAt
      ? Date.now() - queryState.dataUpdatedAt
      : Infinity;
    const response = await userContextService.getUserContext(cacheAge > 30_000);

    if (response.status === 'success' && response.data) {
      // Update store with fresh data
      useUserContextStore.getState().setUserContext(response.data);
      return { userContext: response.data };
    } else {
      useUserContextStore
        .getState()
        .setError(response.message || 'Failed to fetch user context');
      return { userContext: null };
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to refresh user context';
    useUserContextStore.getState().setError(errorMessage);
    return { userContext: null };
  }
};

export const ndaLoaderWithRefresh = async ({ request }: LoaderFunctionArgs) => {
  try {
    usePageLoaderStore.getState().show();

    const { pathname, search } = new URL(request.url);
    const result = await refreshUserContextLoader(pathname + search);

    // Handle redirect response
    if (result instanceof Response) {
      usePageLoaderStore.getState().hide();
      return result;
    }

    // E10 (TDF-1457): under SALES_SN_SKIP the NDA leaves the client journey entirely - the route is
    // unreachable, including from a link the client may have kept. Send them home.
    if (result.userContext?.salesSnSkip) {
      usePageLoaderStore.getState().hide();
      return redirect(PORTAL_ROUTES.HOME);
    }

    const ndaStatus = result.userContext?.ndaStatus;

    if (
      ndaStatus === undefined ||
      !([NdaStatus.NOT_STARTED, NdaStatus.REJECTED] as string[]).includes(
        ndaStatus
      )
    ) {
      usePageLoaderStore.getState().hide();
      return redirect(PORTAL_ROUTES.HOME);
    }

    let ndaStatusDetails: BackendNdaStatusResponse | null = null;
    if (ndaStatus === NdaStatus.REJECTED) {
      try {
        ndaStatusDetails = await ndaStatusService.getNdaStatus();
      } catch {
        // If fetch fails, continue without rejection details
        // Modal will not show if data is null
      }
    }

    usePageLoaderStore.getState().hide();
    return { ndaStatusDetails };
  } catch (error) {
    usePageLoaderStore.getState().hide();
    throw error;
  }
};

/**
 * KYB loader with fresh user-context check
 * Fetches detailed KYB status when status is REJECTED to display rejection modal
 */
export const kybLoaderWithRefresh = async ({ request }: LoaderFunctionArgs) => {
  try {
    // Show page loader for entire operation (user context + KYB status fetch)
    usePageLoaderStore.getState().show();

    const { pathname, search } = new URL(request.url);
    const result = await refreshUserContextLoader(pathname + search);

    // Handle redirect response
    if (result instanceof Response) {
      usePageLoaderStore.getState().hide();
      return result;
    }

    const kybStatus = result.userContext?.kybStatus;

    if (
      kybStatus === undefined ||
      !([KybStatus.NOT_STARTED, KybStatus.REJECTED] as string[]).includes(
        kybStatus
      )
    ) {
      usePageLoaderStore.getState().hide();
      return redirect(PORTAL_ROUTES.HOME);
    }

    // Fetch detailed KYB status if REJECTED to get rejection reason for modal
    // Loader stays visible during this additional API call
    let kybStatusDetails: BackendKybStatusResponse | null = null;
    if (kybStatus === KybStatus.REJECTED) {
      try {
        kybStatusDetails = await kybStatusService.getKybStatus();
      } catch {
        // If fetch fails, continue without rejection details
        // Modal will not show if data is null
      }
    }

    // Hide loader only after ALL operations complete
    usePageLoaderStore.getState().hide();
    return { kybStatusDetails };
  } catch (error) {
    usePageLoaderStore.getState().hide();
    throw error;
  }
};

/**
 * Company Edit loader with fresh user-context check
 * Fetches detailed KYB status when status is REJECTED to display rejection modal
 */
export const companyEditLoaderWithRefresh = async ({
  request,
}: LoaderFunctionArgs) => {
  try {
    // Show page loader for entire operation (user context + KYB status fetch)
    usePageLoaderStore.getState().show();

    const { pathname, search } = new URL(request.url);
    const result = await refreshUserContextLoader(pathname + search);

    // Handle redirect response
    if (result instanceof Response) {
      usePageLoaderStore.getState().hide();
      return result;
    }

    const kybStatus = result.userContext?.kybStatus;

    // Only allow access if KYB status is APPROVED
    if (
      kybStatus === KybStatus.UNDER_REVIEW ||
      kybStatus === KybStatus.PENDING
    ) {
      usePageLoaderStore.getState().hide();
      return redirect(PORTAL_ROUTES.HOME);
    }

    // Fetch detailed KYB status if REJECTED to get rejection reason for modal
    // Loader stays visible during this additional API call
    let kybStatusDetails: BackendKybStatusResponse | null = null;
    if (kybStatus === KybStatus.REJECTED) {
      try {
        kybStatusDetails = await kybStatusService.getKybStatus();
      } catch {
        // If fetch fails, continue without rejection details
        // Modal will not show if data is null
      }
    }

    // Hide loader only after ALL operations complete
    usePageLoaderStore.getState().hide();
    return { kybStatusDetails };
  } catch (error) {
    usePageLoaderStore.getState().hide();
    throw error;
  }
};

/**
 * MSA loader with fresh user-context check
 */
export const msaLoaderWithRefresh = async (
  pathname: string,
  fromPath?: string
) => {
  try {
    // Show page loader for entire operation
    usePageLoaderStore.getState().show();

    const result = await refreshUserContextLoader(fromPath ?? pathname);

    // Handle redirect response
    if (result instanceof Response) {
      usePageLoaderStore.getState().hide();
      return result;
    }

    // E10 (TDF-1457): under SALES_SN_SKIP the MSA leaves the client journey entirely - the route is
    // unreachable, including from a kept link. Send them home.
    if (result.userContext?.salesSnSkip) {
      usePageLoaderStore.getState().hide();
      return redirect(PORTAL_ROUTES.HOME);
    }

    const msaStatus = result.userContext?.msaStatus;

    // Define allowed MSA statuses based on the route
    let allowedMsaStatuses: string[];

    if (pathname.includes('/review')) {
      // For /msa/review route, only allow MSA_DRAFT_RECEIVED status
      allowedMsaStatuses = [MsaStatus.MSA_DRAFT_RECEIVED];
    } else {
      // For /msa/initiate route (default), allow NOT_STARTED, PENDING, and REJECTED
      allowedMsaStatuses = [
        MsaStatus.NOT_STARTED,
        MsaStatus.PENDING,
        MsaStatus.REJECTED,
      ];
    }

    if (!msaStatus || !allowedMsaStatuses.includes(msaStatus)) {
      usePageLoaderStore.getState().hide();
      return redirect(PORTAL_ROUTES.HOME);
    }

    usePageLoaderStore.getState().hide();
    return null;
  } catch (error) {
    usePageLoaderStore.getState().hide();
    throw error;
  }
};
